import React from 'react';

// Small, dependency-free markdown → React for brain pages and other trusted,
// in-house text. Covers what the wiki uses: ATX headings, paragraphs, bullet
// and numbered lists (one nesting level), fenced code, block quotes, pipe
// tables, horizontal rules, inline code / bold / italic / links, and
// [[wikilinks]] (resolved through `resolveWiki(name) → href | null`).
// Nothing is injected as HTML; every node is a React element.

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function inline(text, resolveWiki, keyBase = 'i') {
  const out = [];
  // Order matters: code first so its contents are never re-parsed.
  const re = /(`[^`\n]+`)|(\[\[([^\]|]+)(?:\|([^\]]+))?\]\])|(\*\*([^*\n]+)\*\*)|(\*([^*\n]+)\*)|(\[([^\]\n]+)\]\(([^)\s]+)\))/g;
  let last = 0; let m; let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${k++}`;
    if (m[1]) out.push(<code key={key} className="cs-code">{m[1].slice(1, -1)}</code>);
    else if (m[2]) {
      const name = m[3].trim(); const label = (m[4] || name).trim();
      const href = resolveWiki ? resolveWiki(name) : null;
      out.push(href
        ? <a key={key} href={href} className="md-wikilink">{label}</a>
        : <span key={key} className="md-wikilink is-broken" title="no such page">{label}</span>);
    } else if (m[5]) out.push(<strong key={key}>{inline(m[6], resolveWiki, key)}</strong>);
    else if (m[7]) out.push(<em key={key}>{inline(m[8], resolveWiki, key)}</em>);
    else if (m[9]) {
      const href = m[11];
      const external = /^https?:/i.test(href);
      out.push(<a key={key} href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{m[10]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const slug = (s) => String(s).toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');

export default function Markdown({ text, resolveWiki, className = 'md' }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0; let k = 0;
  const key = () => `b${k++}`;

  while (i < lines.length) {
    const line = lines[i];

    if (/^\s*$/.test(line)) { i += 1; continue; }

    // Fenced code
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const buf = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i += 1; }
      i += 1;
      blocks.push(<pre key={key()} className="md-pre" data-lang={lang || undefined}><code>{buf.join('\n')}</code></pre>);
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.+?)\s*#*$/);
    if (h) {
      const level = h[1].length;
      const Tag = `h${Math.min(level + 1, 6)}`; // page title is the h1; wiki "#" becomes h2
      blocks.push(<Tag key={key()} id={slug(h[2])} className={`md-h md-h${level}`}>{inline(h[2], resolveWiki)}</Tag>);
      i += 1; continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { blocks.push(<hr key={key()} className="cs-divider" />); i += 1; continue; }

    // Table: header row + divider row
    if (/^\s*\|/.test(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const cells = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const head = cells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) { rows.push(cells(lines[i])); i += 1; }
      blocks.push(
        <div key={key()} className="u-scroll-x">
          <table className="cs-table md-table">
            <thead><tr>{head.map((c, j) => <th key={j}>{inline(c, resolveWiki)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, j) => <td key={j}>{inline(c, resolveWiki)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Block quote
    if (/^\s*>/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, '')); i += 1; }
      blocks.push(<blockquote key={key()} className="md-quote"><Markdown text={buf.join('\n')} resolveWiki={resolveWiki} className="" /></blockquote>);
      continue;
    }

    // Lists (bullet or ordered), with one level of indentation nesting
    const li = line.match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/);
    if (li) {
      const ordered = /\d/.test(li[2]);
      const baseIndent = li[1].length;
      const items = [];
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/);
        if (m && m[1].length <= baseIndent) {
          items.push({ text: m[3], children: [] });
          i += 1;
        } else if (m && m[1].length > baseIndent && items.length) {
          items[items.length - 1].children.push(m[3]);
          i += 1;
        } else if (/^\s{2,}\S/.test(lines[i]) && !/^\s*$/.test(lines[i]) && items.length && !m) {
          // continuation line of the previous item
          items[items.length - 1].text += ` ${lines[i].trim()}`;
          i += 1;
        } else break;
      }
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag key={key()} className="md-list">
          {items.map((it, j) => (
            <li key={j}>
              {inline(it.text, resolveWiki, `l${j}`)}
              {it.children.length > 0 && <ul className="md-list">{it.children.map((c, cj) => <li key={cj}>{inline(c, resolveWiki, `c${j}-${cj}`)}</li>)}</ul>}
            </li>
          ))}
        </Tag>,
      );
      continue;
    }

    // Paragraph: run of non-blank, non-special lines
    const buf = [];
    while (i < lines.length && !/^\s*$/.test(lines[i]) && !/^(#{1,6}\s|```|\s*\||\s*>|\s*([-*+]|\d+[.)])\s)/.test(lines[i])) {
      buf.push(lines[i].trim());
      i += 1;
    }
    if (buf.length) blocks.push(<p key={key()}>{inline(buf.join(' '), resolveWiki)}</p>);
    else i += 1; // safety: never stall
  }

  return <div className={className}>{blocks}</div>;
}

// Frontmatter-aware helper: strips a leading `--- … ---` block.
export function stripFrontmatter(text) {
  const m = String(text || '').match(/^---\n[\s\S]*?\n---\n/);
  return m ? text.slice(m[0].length) : text;
}

export { escapeRe };
